import useSWR, { KeyedMutator } from 'swr';
import { useRecoilValue } from 'recoil';

import { useMemo } from 'react';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { get, patch } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import { parseResponse } from 'lib/utils/parser';
import { FollowingsResponse } from 'lib/models/following';
import {
  StudentSocietyAttributes,
  StudentSocietyResponse,
} from 'lib/models/student-society';
import { CompanyAttributes, CompanyResponse } from 'lib/models/company';
import { FilteredOpportunitiesResponse } from 'lib/models/filteredTouchpoint';
import { OpportunitiesResponse } from 'lib/models/opportunity';
import { useNotification } from './useNotification';

const notificationInstance = useNotification();

interface ReturnType {
  savedTouchpoints: FilteredOpportunitiesResponse | undefined;
  appliedTouchpoints: FilteredOpportunitiesResponse | undefined;
  acceptedTouchpoints: FilteredOpportunitiesResponse | undefined;
  followedCompanies: Array<CompanyAttributes>;
  followedSocieties: Array<StudentSocietyAttributes>;
  savedTouchpointsMutate: KeyedMutator<FilteredOpportunitiesResponse>;
  appliedTouchpointsMutate: KeyedMutator<FilteredOpportunitiesResponse>;
  acceptedTouchpointsMutate: KeyedMutator<FilteredOpportunitiesResponse>;
  followedCompaniesMutate: KeyedMutator<FollowingsResponse>;
  followedSocietiesMutate: KeyedMutator<FollowingsResponse>;
  isFollowedSocietiesLoading: boolean;
  isFollowedCompaniesLoading: boolean;
  onMoveCard: (
    id: string,
    headers: Headers,
    status: string,
    isFromApplication?: boolean
  ) => Promise<OpportunitiesResponse | undefined>;
}

export const useFilteredTouchpoints = (): ReturnType => {
  const { headers } = useRecoilValue(httpHeadersState);

  const savedTouchpointsPathname = `${API_ROUTES.CAMPAIGNS}?group_by=touchpointable&action_type=saved`;
  const { data: savedTouchpoints, mutate: savedTouchpointsMutate } =
    useSWR<FilteredOpportunitiesResponse>(
      [savedTouchpointsPathname, headers],
      get
    );

  const appliedTouchpointsPathname = `${API_ROUTES.CAMPAIGNS}?group_by=touchpointable&action_type=applied`;
  const { data: appliedTouchpoints, mutate: appliedTouchpointsMutate } =
    useSWR<FilteredOpportunitiesResponse>(
      [appliedTouchpointsPathname, headers],
      get
    );

  const acceptedTouchpointsPathname = `${API_ROUTES.CAMPAIGNS}?group_by=touchpointable&action_type=accepted`;
  const { data: acceptedTouchpoints, mutate: acceptedTouchpointsMutate } =
    useSWR<FilteredOpportunitiesResponse>(
      [acceptedTouchpointsPathname, headers],
      get
    );

  const followedCompanyPathname = `${API_ROUTES.FOLLOW}?with_details=true&followable_types[]=Company`;
  const {
    data: followedCompanyResponse,
    mutate: followedCompaniesMutate,
    isLoading: isFollowedCompaniesLoading,
  } = useSWR<FollowingsResponse>([followedCompanyPathname, headers], get);

  const followedSocietyPathname = `${API_ROUTES.FOLLOW}?with_details=true&followable_types[]=StudentSociety`;
  const {
    data: followedSocietyResponse,
    mutate: followedSocietiesMutate,
    isLoading: isFollowedSocietiesLoading,
  } = useSWR<FollowingsResponse>([followedSocietyPathname, headers], get);

  const followedCompanies = useMemo(() => {
    return followedCompanyResponse
      ? followedCompanyResponse.data
          .map((item) => item.attributes)
          .map((company) => {
            const {
              id: entityId,
              followable: followableResponse,
              unseen_count: unseen_count,
            } = company;
            const followable = parseResponse<CompanyAttributes>(
              followableResponse as CompanyResponse
            );
            return { ...followable, entityId, unseen_count };
          })
      : [];
  }, [followedCompanyResponse]);

  const followedSocieties = useMemo(() => {
    return followedSocietyResponse
      ? followedSocietyResponse.data
          .map((item) => item.attributes)
          .map((society) => {
            const {
              id: entityId,
              followable: followableResponse,
              unseen_count: unseen_count,
            } = society;
            const followable = parseResponse<StudentSocietyAttributes>(
              followableResponse as StudentSocietyResponse
            );
            return { ...followable, entityId, unseen_count };
          })
      : [];
  }, [followedSocietyResponse]);

  const onMoveCard = async (
    id: string,
    headers: Headers,
    status: string,
    isFromApplication?: boolean
  ) => {
    const pathname = `${API_ROUTES.CANDIDATE_TOUCHPOINT}/${id}`;
    const body = {
      move_to: status,
      from_application: isFromApplication || false,
    };
    try {
      const response = await patch<OpportunitiesResponse>(
        pathname,
        body,
        headers
      );
      return response;
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  return {
    savedTouchpoints,
    appliedTouchpoints,
    acceptedTouchpoints,
    followedCompanies,
    followedSocieties,
    savedTouchpointsMutate,
    appliedTouchpointsMutate,
    acceptedTouchpointsMutate,
    followedCompaniesMutate,
    followedSocietiesMutate,
    onMoveCard,
    isFollowedCompaniesLoading,
    isFollowedSocietiesLoading,
  };
};
